import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { NavActions } from '../components/NavBar/NavActions';
import { Toast } from '../components/Toast';
import type { ToastProps } from '../components/Toast';
import { Button } from '../components/SubscriptionAppButton';
import { IconButton } from '../components/SubscriptionAppIconButton';
import { useLoyaltyAudit } from '../contexts/LoyaltyAuditContext';
import { GripVertical, Pencil, Trash2, Plus, X, ChevronDown, Check, AlertTriangle } from 'lucide-react';
import { TierBenefitsEditor } from '../components/loyalty/TierBenefitsEditor';
import { NO_TIER_DISCOUNT, summariseTierBenefits } from '../utils/tierBenefits';
import type { TierBenefitsPatch, TierDiscount, TierFreeShipping } from '../types/loyaltyTiers';
// Redemption value lives in Program Settings; used here to show the true give-back.
const REDEMPTION_VALUE_PER_POINT = 0.01;
interface Tier {
  id: string;
  name: string;
  threshold: number;
  multiplier: number;
  color: string;
  benefits: string[];
  discount: TierDiscount;
  freeShipping: TierFreeShipping;
}
const NEWS_PERK = 'Exclusive news, offers and wine releases';
const EVENTS_PERK = 'Priority invitations to member events';
const tierDiscount = (percent: number, appliesTo: TierDiscount['appliesTo'], collections: string[] = []): TierDiscount => ({
  enabled: true,
  percent,
  appliesTo,
  collections,
  productIds: []
});
const cloneDiscount = (discount: TierDiscount): TierDiscount => ({
  ...discount,
  collections: [...discount.collections],
  productIds: [...discount.productIds]
});
const cloneTier = (tier: Tier): Tier => ({
  ...tier,
  benefits: [...tier.benefits],
  discount: cloneDiscount(tier.discount),
  freeShipping: {
    ...tier.freeShipping
  }
});
const COLOR_OPTIONS = [{
  name: 'Green',
  value: '#10B981'
}, {
  name: 'Blue',
  value: '#3B82F6'
}, {
  name: 'Purple',
  value: '#6F42C1'
}, {
  name: 'Gold',
  value: '#F59E0B'
}, {
  name: 'Red',
  value: '#EF4444'
}, {
  name: 'Grey',
  value: '#94A3B8'
}];
const ENROLMENT_POLICIES = [{
  id: 'everyone',
  title: 'Auto-enrol everyone',
  desc: 'Every customer joins the program automatically.'
}, {
  id: 'subscribers',
  title: 'Subscribers only',
  desc: 'Only active subscription members are enrolled.'
}, {
  id: 'tag',
  title: 'Tag-based',
  desc: 'Enrol customers who have a specific tag.'
}, {
  id: 'optin',
  title: 'Opt-in only',
  desc: 'Customers join only when they opt in themselves.'
}, {
  id: 'hybrid',
  title: 'Hybrid',
  desc: 'Auto-enrol one group and let everyone else opt in.'
}];
// Windows only apply to Annual Spend — the period spend is measured over.
const QUALIFICATION_WINDOWS = [{
  id: 'rolling',
  title: 'Rolling 12 months',
  desc: 'The trailing 12 months from the evaluation date.'
}, {
  id: 'calendar',
  title: 'Calendar year',
  desc: '1 January to 31 December.'
}, {
  id: 'financial',
  title: 'Financial year',
  desc: '1 July to 30 June — common in Australia and New Zealand.'
}, {
  id: 'custom',
  title: 'Custom start month',
  desc: 'Set your own 12-month window start.'
}];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
// Pulled from Shopify in the real build; mocked here for the prototype.
const SHOPIFY_COLLECTIONS = ['Estate', 'Red Wines', 'White Wines', 'Rosé', 'Sparkling', 'Reserve', 'Library Releases', 'Merchandise & Glassware'];
// Example existing in-app product tags offered as quick picks.
const SUGGESTED_PRODUCT_TAGS = ['Reserve', 'Limited Release', 'Club Exclusive', 'Premium'];
// Experiences configured in the booking platform; pulled via the integration in the real build.
const NOWBOOKIT_EXPERIENCES = ['Cellar Door Tasting', 'Premium Flight & Cheese', 'Vineyard Walk & Taste', 'Winemaker Lunch'];
const WINE_TEMPLATE: Tier[] = [{
  id: 'enthusiast',
  name: 'Enthusiast',
  threshold: 0,
  multiplier: 1.0,
  color: '#10B981',
  benefits: [NEWS_PERK],
  discount: tierDiscount(10, 'all'),
  freeShipping: {
    mode: 'off',
    minimum: 0
  }
}, {
  id: 'connoisseur',
  name: 'Connoisseur',
  threshold: 500,
  multiplier: 1.1,
  color: '#3B82F6',
  benefits: [NEWS_PERK],
  discount: tierDiscount(15, 'collections', ['Estate']),
  freeShipping: {
    mode: 'over',
    minimum: 249
  }
}, {
  id: 'sommelier',
  name: 'Sommelier',
  threshold: 2000,
  multiplier: 1.25,
  color: '#6F42C1',
  benefits: [EVENTS_PERK, NEWS_PERK],
  discount: tierDiscount(20, 'collections', ['Estate']),
  freeShipping: {
    mode: 'over',
    minimum: 249
  }
}, {
  id: 'vintner',
  name: 'Vintner',
  threshold: 5000,
  multiplier: 1.5,
  color: '#F59E0B',
  benefits: [EVENTS_PERK, NEWS_PERK],
  discount: tierDiscount(20, 'collections', ['Estate', 'Reserve']),
  freeShipping: {
    mode: 'all',
    minimum: 0
  }
}];
export function LoyaltyRulesPage() {
  const {
    addAuditEvent
  } = useLoyaltyAudit();
  const [searchParams] = useSearchParams();
  const tierRowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [highlightedTierId, setHighlightedTierId] = useState<string | null>(null);
  const [calculationMethod, setCalculationMethod] = useState('Points');
  // Thresholds render as points or dollars depending on the calculation method.
  const isPointsMethod = calculationMethod === 'Points';
  // A qualification window only makes sense for Annual Spend.
  const isAnnualSpendMethod = calculationMethod === 'Annual Spend';
  const [qualificationWindow, setQualificationWindow] = useState('rolling');
  const [customStartMonth, setCustomStartMonth] = useState('July');
  const [reviewFrequency, setReviewFrequency] = useState('daily');
  // Fixed windows close on a date, so downgrades are assessed at period close.
  const isFixedWindow = isAnnualSpendMethod && qualificationWindow !== 'rolling';
  const [downgradePolicy, setDowngradePolicy] = useState('permanent');
  const [expandedTierId, setExpandedTierId] = useState<string | null>(null);
  const [newTierId, setNewTierId] = useState<string | null>(null);
  const [earningModel, setEarningModel] = useState('everything');
  const [baseEarningRate, setBaseEarningRate] = useState('5');
  // Product Earning Model — selection config
  const [earningTags, setEarningTags] = useState<string[]>([]);
  const [newEarningTag, setNewEarningTag] = useState('');
  const [tieredCollections, setTieredCollections] = useState<Array<{
    id: string;
    collection: string;
    rate: string;
  }>>([]);
  // Enrolment settings (4.7)
  const [enrolmentPolicy, setEnrolmentPolicy] = useState('subscribers');
  const [enrolmentTags, setEnrolmentTags] = useState<string[]>(['Wine Club']);
  const [newEnrolmentTag, setNewEnrolmentTag] = useState('');
  const [hybridAutoGroup, setHybridAutoGroup] = useState('subscribers');
  const [welcomeEmailOnEnrol, setWelcomeEmailOnEnrol] = useState(true);
  const [autoIssuePassOnEnrol, setAutoIssuePassOnEnrol] = useState(true);
  const [bonusToggles, setBonusToggles] = useState<Record<string, boolean>>({
    signup: true,
    referrer: true,
    referee: true,
    review: true,
    birthday: true,
    order3: true,
    order6: true,
    order12: true
  });
  const [experienceEnabled, setExperienceEnabled] = useState(true);
  const [bookingConnected, setBookingConnected] = useState(false);
  const [bookingMappings, setBookingMappings] = useState<Record<string, string>>({});
  const [toasts, setToasts] = useState<ToastProps[]>([]);
  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };
  const addEnrolmentTag = () => {
    const v = newEnrolmentTag.trim();
    if (v && !enrolmentTags.includes(v)) {
      setEnrolmentTags(prev => [...prev, v]);
    }
    setNewEnrolmentTag('');
  };
  const handleSave = () => {
    // Live audit (pass 3): emit a Settings event for the enrolment policy.
    // Entity is null — this is a program-wide setting, not tied to a member.
    // Prior value isn't tracked in the prototype, so before is '—'.
    const currentPolicyLabel = ENROLMENT_POLICIES.find(p => p.id === enrolmentPolicy)?.title ?? enrolmentPolicy;
    addAuditEvent({
      eventType: 'Settings',
      activity: `Enrolment policy updated — ${currentPolicyLabel}`,
      entity: null,
      entityType: null,
      changes: [{
        field: 'Enrolment Policy',
        before: '—',
        after: currentPolicyLabel
      }]
    });
    setToasts(prev => [...prev, {
      id: Date.now().toString(),
      type: 'success' as const,
      title: 'Rules saved',
      message: 'Your loyalty rules and tier configuration have been saved successfully.',
      onClose: removeToast
    }]);
  };
  const [addingBenefitForTier, setAddingBenefitForTier] = useState<string | null>(null);
  const [newBenefitText, setNewBenefitText] = useState('');
  const [tiers, setTiers] = useState<Tier[]>(() => WINE_TEMPLATE.map(cloneTier));
  // Bumped each time an edit panel opens, so its form controls remount from the seeded values
  const [editSession, setEditSession] = useState(0);
  // One tier means a flat points program — nowhere to move up or down to.
  const isSingleTier = tiers.length === 1;
  // Editing state per tier
  const [editValues, setEditValues] = useState<Record<string, {
    name: string;
    threshold: string;
    multiplier: string;
    color: string;
    benefits: string[];
    discount: TierDiscount;
    freeShipping: TierFreeShipping;
  }>>({});
  useEffect(() => {
    if (toasts.length > 0) {
      const timer = setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== prev[prev.length - 1].id));
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [toasts]);
  // Deep-link support: /loyalty/rules?tier=<id> scrolls to and briefly
  // highlights that tier row. Used by the Contact Detail Tier History links (D1).
  useEffect(() => {
    const tierParam = searchParams.get('tier');
    if (!tierParam) return;
    if (!tiers.some(t => t.id === tierParam)) return;
    setHighlightedTierId(tierParam);
    const el = tierRowRefs.current[tierParam];
    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
    const timer = setTimeout(() => setHighlightedTierId(null), 2600);
    return () => clearTimeout(timer);
  }, [searchParams, tiers]);
  // Seeds the edit panel from the tier exactly as stored (a fresh deep copy each time it opens)
  const startEditing = (tier: Tier) => {
    const copy = cloneTier(tier);
    setEditValues(prev => ({
      ...prev,
      [tier.id]: {
        name: copy.name,
        threshold: String(copy.threshold),
        multiplier: String(copy.multiplier),
        color: copy.color,
        benefits: copy.benefits,
        discount: copy.discount,
        freeShipping: copy.freeShipping
      }
    }));
    setEditSession(session => session + 1);
    setExpandedTierId(tier.id);
  };
  const finishEditing = (tierId: string) => {
    const vals = editValues[tierId];
    if (vals && !vals.name.trim()) return;
    if (vals) {
      setTiers(prev => prev.map(t => t.id === tierId ? {
        ...t,
        name: vals.name || t.name,
        threshold: Number(vals.threshold) || 0,
        multiplier: Number(vals.multiplier) || 1,
        color: vals.color,
        benefits: vals.benefits,
        discount: vals.discount,
        freeShipping: vals.freeShipping
      } : t));
    }
    setExpandedTierId(null);
    setNewTierId(null);
    setAddingBenefitForTier(null);
    setNewBenefitText('');
  };
  const cancelEditing = (tierId: string) => {
    if (tierId === newTierId) {
      setTiers(prev => prev.filter(t => t.id !== tierId));
      setNewTierId(null);
    }
    setExpandedTierId(null);
    setAddingBenefitForTier(null);
    setNewBenefitText('');
  };
  const updateEditValue = (tierId: string, field: string, value: string | string[]) => {
    setEditValues(prev => ({
      ...prev,
      [tierId]: {
        ...prev[tierId],
        [field]: value
      }
    }));
  };
  const updateEditPerks = (tierId: string, patch: TierBenefitsPatch) => {
    setEditValues(prev => ({
      ...prev,
      [tierId]: {
        ...prev[tierId],
        ...patch
      }
    }));
  };
  const removeBenefit = (tierId: string, benefit: string) => {
    setEditValues(prev => ({
      ...prev,
      [tierId]: {
        ...prev[tierId],
        benefits: prev[tierId].benefits.filter(b => b !== benefit)
      }
    }));
  };
  const addBenefit = (tierId: string) => {
    if (newBenefitText.trim()) {
      setEditValues(prev => ({
        ...prev,
        [tierId]: {
          ...prev[tierId],
          benefits: [...prev[tierId].benefits, newBenefitText.trim()]
        }
      }));
      setNewBenefitText('');
      setAddingBenefitForTier(null);
    }
  };
  const deleteTier = (tierId: string) => {
    setTiers(prev => prev.filter(t => t.id !== tierId));
    setDeletingTierId(null);
    if (expandedTierId === tierId) setExpandedTierId(null);
  };
  const addNewTier = () => {
    if (tiers.length >= 5) return;
    const newId = `tier-${Date.now()}`;
    const newTier: Tier = {
      id: newId,
      name: '',
      threshold: 0,
      multiplier: 1.0,
      color: '#94A3B8',
      benefits: [],
      discount: cloneDiscount(NO_TIER_DISCOUNT),
      freeShipping: {
        mode: 'off',
        minimum: 0
      }
    };
    setTiers(prev => [...prev, newTier]);
    setNewTierId(newId);
    startEditing(newTier);
  };
  const applyWineTemplate = () => {
    setTiers(WINE_TEMPLATE.map(cloneTier));
    setShowTemplateConfirm(false);
    setExpandedTierId(null);
  };
  const [bonusEvents, setBonusEvents] = useState<Array<{
    id: string;
    name: string;
    points: number;
    note?: string;
    custom?: boolean;
    confirmed?: boolean;
  }>>([{
    id: 'signup',
    name: 'Sign-Up Bonus',
    points: 500
  }, {
    id: 'referrer',
    name: 'Referral — Referrer',
    points: 1000
  }, {
    id: 'referee',
    name: 'Referral — Referee',
    points: 500
  }, {
    id: 'review',
    name: 'Product Review',
    points: 50,
    note: 'Cap 5 per month'
  }, {
    id: 'birthday',
    name: 'Birthday Bonus',
    points: 200
  }, {
    id: 'order3',
    name: 'After 3rd Order',
    points: 100
  }, {
    id: 'order6',
    name: 'After 6th Order',
    points: 200
  }, {
    id: 'order12',
    name: 'After 12th Order',
    points: 500
  }]);
  const [experienceTypes, setExperienceTypes] = useState<Array<{
    name: string;
    points: number;
    custom?: boolean;
    confirmed?: boolean;
  }>>([{
    name: 'Cellar Door Visit',
    points: 50
  }, {
    name: 'Standard Tasting',
    points: 100
  }, {
    name: 'Premium/Reserve Tasting',
    points: 200
  }, {
    name: 'Vineyard Tour',
    points: 150
  }, {
    name: 'Blending Workshop',
    points: 300
  }, {
    name: 'Winemaker Dinner',
    points: 500
  }]);
  const toggleBonus = (id: string) => {
    setBonusToggles(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const addCustomMilestone = () => {
    const id = `custom-milestone-${Date.now()}`;
    setBonusEvents(prev => [...prev, {
      id,
      name: '',
      points: 100,
      custom: true,
      confirmed: false
    }]);
    setBonusToggles(prev => ({
      ...prev,
      [id]: true
    }));
  };
  const updateMilestoneName = (id: string, name: string) => {
    setBonusEvents(prev => prev.map(e => e.id === id ? {
      ...e,
      name
    } : e));
  };
  const removeMilestone = (id: string) => {
    setBonusEvents(prev => prev.filter(e => e.id !== id));
    setBonusToggles(prev => {
      const next = {
        ...prev
      };
      delete next[id];
      return next;
    });
  };
  const confirmMilestone = (id: string) => {
    setBonusEvents(prev => prev.map(e => e.id === id && e.name.trim() ? {
      ...e,
      confirmed: true
    } : e));
  };
  const addExperienceType = () => {
    setExperienceTypes(prev => [...prev, {
      name: '',
      points: 100,
      custom: true,
      confirmed: false
    }]);
  };
  const updateExperienceName = (index: number, name: string) => {
    setExperienceTypes(prev => prev.map((e, i) => i === index ? {
      ...e,
      name
    } : e));
  };
  const removeExperienceType = (index: number) => {
    setExperienceTypes(prev => prev.filter((_, i) => i !== index));
  };
  const confirmExperience = (index: number) => {
    setExperienceTypes(prev => prev.map((e, i) => i === index && e.name.trim() ? {
      ...e,
      confirmed: true
    } : e));
  };
  const addEarningTag = (tag?: string) => {
    const v = (tag ?? newEarningTag).trim();
    if (v && !earningTags.includes(v)) {
      setEarningTags(prev => [...prev, v]);
    }
    setNewEarningTag('');
  };
  const removeEarningTag = (tag: string) => {
    setEarningTags(prev => prev.filter(t => t !== tag));
  };
  const addTieredCollection = () => {
    setTieredCollections(prev => [...prev, {
      id: `tc-${Date.now()}`,
      collection: '',
      rate: ''
    }]);
  };
  const updateTieredCollection = (id: string, field: 'collection' | 'rate', value: string) => {
    setTieredCollections(prev => prev.map(r => r.id === id ? {
      ...r,
      [field]: value
    } : r));
  };
  const removeTieredCollection = (id: string) => {
    setTieredCollections(prev => prev.filter(r => r.id !== id));
  };
  const connectBooking = () => {
    setBookingConnected(true);
    setBookingMappings({
      'Cellar Door Tasting': 'Standard Tasting',
      'Premium Flight & Cheese': 'Premium/Reserve Tasting'
    });
  };
  const disconnectBooking = () => {
    setBookingConnected(false);
  };
  const setBookingMapping = (nbiExp: string, target: string) => {
    setBookingMappings(prev => ({
      ...prev,
      [nbiExp]: target
    }));
  };
  const [showTemplateConfirm, setShowTemplateConfirm] = useState(false);
  const [deletingTierId, setDeletingTierId] = useState<string | null>(null);
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-10275" data-mp="3cqtifqjw7u1p">
      {/* Header */}
      <div className="mb-6" data-id="element-10276" data-mp="1gjr6plygyg67">
        <div className="w-full h-16 bg-white border-b border-[#CBD5E1] px-6 flex items-center gap-4" data-id="element-10277" data-mp="1gnoqm9svfbr5">
          <h1 className="text-lg font-semibold text-[#0F172A] m-0" data-id="element-10278" data-mp="16n4l2uybjl34">
            Rules & Tiers
          </h1>
          <div className="flex-1" data-id="element-10279" data-mp="1blze0pq02q0y" />
          <Button variant="primary" size="md" onClick={handleSave} data-id="element-10280" data-mp="1bnag8l3srppy">
            Save Changes
          </Button>
          <NavActions data-id="element-10281" data-mp="1zcbuwq383x3o" />
        </div>
      </div>

      <div className="max-w-4xl mx-auto space-y-6" data-id="element-10282" data-mp="24an4r0tiq7lw">
        {/* SECTION 0 — ENROLMENT */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 shadow-sm" data-id="element-10283" data-mp="1pm0fa6a6fzgy">
          <h2 className="text-base font-semibold text-[#0F172A] mb-1" data-id="element-10284" data-mp="1pjw9tjwmqsek">
            Enrolment
          </h2>
          <p className="text-xs text-[#94A3B8] mb-4" data-id="element-10285" data-mp="1uiqukre5qr1b">
            Controls which customers join the loyalty program.
          </p>

          {/* Policy radio group */}
          <div className="space-y-4" data-id="element-10286" data-mp="2n2z3ldkyqqra">
            {ENROLMENT_POLICIES.map(option => <div key={option.id} data-id="element-10287" data-mp="bldqtqv5j26o">
                <label className="flex items-center gap-3 cursor-pointer mb-1" data-id="element-10288" data-mp="bkkqb7msd8wz">
                  <div className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${enrolmentPolicy === option.id ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} onClick={() => setEnrolmentPolicy(option.id)} data-id="element-10289" data-mp="6mt2opt6c8yl">
                    {enrolmentPolicy === option.id && <div className="w-2 h-2 rounded-full bg-[#0EA5E9]" data-id="element-10290" data-mp="3os5qre5rprv7" />}
                  </div>
                  <span className="text-sm font-medium text-[#0F172A]" data-id="element-10291" data-mp="3jue34wc5orwt">
                    {option.title}
                  </span>
                </label>
                <p className="text-xs text-[#64748B] ml-7" data-id="element-10292" data-mp="1m51xcf58dr6">{option.desc}</p>

                {/* Tag-based sub-config */}
                {enrolmentPolicy === 'tag' && option.id === 'tag' && <div className="ml-7 mt-3" data-id="element-10293" data-mp="1d1tnq0gnv6qe">
                    <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-10294" data-mp="184bm6v4z3tsf">
                      Enrol customers tagged
                    </label>
                    <div className="flex flex-wrap items-center gap-2" data-id="element-10295" data-mp="xv67lym48evr">
                      {enrolmentTags.map(tag => <div key={tag} className="flex items-center gap-1 bg-[#E0F2FE] text-[#0EA5E9] px-2.5 py-1 rounded-full text-xs font-medium" data-id="element-10296" data-mp="sxnv5y8w3hj0">
                          {tag}
                          <button onClick={() => setEnrolmentTags(prev => prev.filter(t => t !== tag))} className="hover:text-[#0369A1]" data-id="element-10297" data-mp="12t7r2kngeg7r">
                            <X className="w-3 h-3" data-id="element-10298" data-mp="1grfiye5murc2" />
                          </button>
                        </div>)}
                      <input type="text" value={newEnrolmentTag} onChange={e => setNewEnrolmentTag(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addEnrolmentTag();
                  }
                }} placeholder="Add tag" className="h-7 w-28 px-2 border border-[#CBD5E1] rounded-md text-xs text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-10299" data-mp="1qjbt4nkzu7wn" />
                    </div>
                    <p className="text-xs text-[#94A3B8] mt-1.5" data-id="element-10300" data-mp="2j5tcc0scu11i">
                      Matches the customer's Shopify tags. Add one or more.
                    </p>
                  </div>}

                {/* Opt-in sub-config */}
                {enrolmentPolicy === 'optin' && option.id === 'optin' && <div className="ml-7 mt-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-md px-3 py-2.5" data-id="element-10301" data-mp="1vgh8lgfsql6l">
                    <p className="text-xs text-[#64748B]" data-id="element-10302" data-mp="206xkux7vs0rd">
                      Customers join from the storefront loyalty block or the
                      customer portal. Make sure at least one opt-in surface is
                      switched on.
                    </p>
                  </div>}

                {/* Hybrid sub-config */}
                {enrolmentPolicy === 'hybrid' && option.id === 'hybrid' && <div className="ml-7 mt-3" data-id="element-10303" data-mp="1hgg4ls48ixmw">
                    <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-10304" data-mp="1hg6fe6r0kui9">
                      Auto-enrol
                    </label>
                    <div className="relative w-[220px]" data-id="element-10305" data-mp="3eff4jzxn9gvx">
                      <select value={hybridAutoGroup} onChange={e => setHybridAutoGroup(e.target.value)} className="w-full h-9 pl-3 pr-8 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none appearance-none bg-white" data-id="element-10306" data-mp="3edvmc3sfgvri">
                        <option value="subscribers" data-id="element-10307" data-mp="5ykbfc6nfav1">Subscribers</option>
                        <option value="tagged" data-id="element-10308" data-mp="1yteeqqj3yi2">Tagged customers</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8] pointer-events-none" data-id="element-10309" data-mp="3t307ntuirg3u" />
                    </div>
                    {hybridAutoGroup === 'tagged' && <div className="mt-3" data-id="element-10310" data-mp="2hpxp4t8v0gh1">
                        <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-10311" data-mp="2hmtdjat2eigc">
                          Auto-enrol customers tagged
                        </label>
                        <div className="flex flex-wrap items-center gap-2" data-id="element-10312" data-mp="22nfsxmpc9e5g">
                          {enrolmentTags.map(tag => <div key={tag} className="flex items-center gap-1 bg-[#E0F2FE] text-[#0EA5E9] px-2.5 py-1 rounded-full text-xs font-medium" data-id="element-10313" data-mp="27l8ws9365w2k">
                              {tag}
                              <button onClick={() => setEnrolmentTags(prev => prev.filter(t => t !== tag))} className="hover:text-[#0369A1]" data-id="element-10314" data-mp="2hcbx38o9y70s">
                                <X className="w-3 h-3" data-id="element-10315" data-mp="2qbgta8cd5o9s" />
                              </button>
                            </div>)}
                          <input type="text" value={newEnrolmentTag} onChange={e => setNewEnrolmentTag(e.target.value)} onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addEnrolmentTag();
                    }
                  }} placeholder="Add tag" className="h-7 w-28 px-2 border border-[#CBD5E1] rounded-md text-xs text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-10316" data-mp="2vaabuf6ic5gt" />
                        </div>
                      </div>}
                    <p className="text-xs text-[#94A3B8] mt-1.5" data-id="element-10317" data-mp="2rfqrtwub852t">
                      Everyone outside this group can opt in themselves.
                    </p>
                  </div>}
              </div>)}
          </div>

          {/* Helper line — ties back to the Not enrolled state */}
          <div className="mt-4 bg-[#F0F9FF] border border-[#BAE6FD] rounded-md px-4 py-2.5" data-id="element-10318" data-mp="2cozzmnot33z6">
            <p className="text-xs text-[#0369A1]" data-id="element-10319" data-mp="28opv7fgnwlmf">
              Customers outside the policy appear as{' '}
              <span className="font-medium" data-id="element-10320" data-mp="2mfup00xcmjou">Not enrolled</span> on their
              contact's Loyalty tab until added manually.
            </p>
          </div>

          {/* When someone is enrolled — consequences subgroup */}
          <div className="mt-6 pt-6 border-t border-[#E2E8F0]" data-id="element-10321" data-mp="1oz5ms64hlfex">
            <h3 className="text-sm font-semibold text-[#0F172A] mb-3" data-id="element-10322" data-mp="1oyvw0gbmb486">
              When someone is enrolled
            </h3>
            <div className="space-y-1" data-id="element-10323" data-mp="97tmnkvd71k6">
              <div className="flex items-center justify-between py-2" data-id="element-10324" data-mp="98va6otkhgis">
                <div className="pr-4" data-id="element-10325" data-mp="3rm2x0ag3y0ef">
                  <span className="text-sm font-medium text-[#0F172A]" data-id="element-10326" data-mp="3rn45c75qo8sg">
                    Send a welcome email
                  </span>
                  <p className="text-xs text-[#64748B] mt-0.5" data-id="element-10327" data-mp="d5g7re1zdts">
                    Emails the member when they join the program.
                  </p>
                </div>
                <button onClick={() => setWelcomeEmailOnEnrol(!welcomeEmailOnEnrol)} className={`w-11 h-6 rounded-full transition-colors duration-200 ease-in-out relative flex-shrink-0 ${welcomeEmailOnEnrol ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-10328" data-mp="s5wiaiif3wqy">
                  <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ease-in-out ${welcomeEmailOnEnrol ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-10329" data-mp="2yxf4btt148qu" />
                </button>
              </div>
              <div className="flex items-center justify-between py-2" data-id="element-10330" data-mp="2jtm72v7g6cru">
                <div className="pr-4" data-id="element-10331" data-mp="2ftlfe8vc852u">
                  <span className="text-sm font-medium text-[#0F172A]" data-id="element-10332" data-mp="2fun43g5a0q31">
                    Issue a wallet pass automatically
                  </span>
                  <p className="text-xs text-[#64748B] mt-0.5" data-id="element-10333" data-mp="263ipihjij78q">
                    Creates and sends a digital wallet pass on enrolment.
                  </p>
                </div>
                <button onClick={() => setAutoIssuePassOnEnrol(!autoIssuePassOnEnrol)} className={`w-11 h-6 rounded-full transition-colors duration-200 ease-in-out relative flex-shrink-0 ${autoIssuePassOnEnrol ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-10334" data-mp="1w4t3cckl30mo">
                  <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ease-in-out ${autoIssuePassOnEnrol ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-10335" data-mp="1n5fvtpg0sm4l" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 1 — TIERS */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 shadow-sm" data-id="element-10336" data-mp="3ozkll75yd46z">
          <h2 className="text-base font-semibold text-[#0F172A] mb-1" data-id="element-10337" data-mp="3tyf56bbquws5">Tiers</h2>
          {isSingleTier && <p className="text-xs text-[#64748B] mb-4" data-id="element-10338" data-mp="2nx897o4gtt6">
              A single tier runs as a flat points program. You can add more tiers
              at any time.
            </p>}

          {/* Top Row */}
          <div className={`flex items-center gap-4 mb-6 ${isSingleTier ? '' : 'mt-4'}`} data-id="element-10339" data-mp="364nffpvfxcwg">
            <div className="flex items-center gap-2" data-id="element-10340" data-mp="3b1vzxx429t0v">
              <span className="text-sm font-medium text-[#0F172A]" data-id="element-10341" data-mp="3g0h2422vimry">
                Calculation Method
              </span>
              <div className="relative w-[200px]" data-id="element-10342" data-mp="3k3vkv0i75h93">
                <select value={calculationMethod} onChange={e => setCalculationMethod(e.target.value)} className="w-full h-9 pl-3 pr-8 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none appearance-none bg-white" data-id="element-10343" data-mp="varp7ulqdb1w">
                  <option data-id="element-10344" data-mp="1gesh4d8e1egu">Annual Spend</option>
                  <option data-id="element-10345" data-mp="1gfv9yqp9tqze">Lifetime Spend</option>
                  <option data-id="element-10346" data-mp="1v3gbwhaetk5q">Points</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8] pointer-events-none" data-id="element-10347" data-mp="1v4reids4clv9" />
              </div>
            </div>

            <div className="flex-1 flex justify-end relative" data-id="element-10348" data-mp="2433bqynhqhwo">
              <Button variant="secondary" size="sm" onClick={() => setShowTemplateConfirm(true)} data-id="element-10349" data-mp="1z51t2etz8klq">
                Use Wine Template
              </Button>

              {/* Wine Template Confirmation Popover */}
              {showTemplateConfirm && <div className="absolute top-full right-0 mt-2 w-72 bg-white border border-[#E2E8F0] rounded-lg shadow-lg p-4 z-20" data-id="element-10350" data-mp="29497z6gd0nay">
                  <p className="text-sm text-[#0F172A] mb-3" data-id="element-10351" data-mp="jojfunft5uov">
                    This will replace your current tiers. Continue?
                  </p>
                  <div className="flex justify-end gap-2" data-id="element-10352" data-mp="2qca2anvie61f">
                    <Button variant="ghost" size="sm" onClick={() => setShowTemplateConfirm(false)} data-id="element-10353" data-mp="2qewa8ombank6">
                      Cancel
                    </Button>
                    <Button variant="primary" size="sm" onClick={applyWineTemplate} data-id="element-10354" data-mp="2glxk32b62oyg">
                      Replace Tiers
                    </Button>
                  </div>
                </div>}
            </div>
          </div>

          {/* Qualification Window — Annual Spend only, and only with a ladder */}
          {isAnnualSpendMethod && !isSingleTier && <div className="mb-6" data-id="element-10355" data-mp="1xnurkdjeu9vw">
              <span className="text-sm font-medium text-[#0F172A] block mb-2" data-id="element-10356" data-mp="1xowepgdpamtz">
                Qualification window
              </span>
              <div className="space-y-3" data-id="element-10357" data-mp="22n7lfi8p8h7z">
                {QUALIFICATION_WINDOWS.map(option => <div key={option.id} data-id="element-10358" data-mp="3s2f6saoypd7w">
                    <label className="flex items-center gap-3 cursor-pointer mb-1" data-id="element-10359" data-mp="vkshb58yc8v">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${qualificationWindow === option.id ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} onClick={() => setQualificationWindow(option.id)} data-id="element-10360" data-mp="ti2gr43gj45">
                        {qualificationWindow === option.id && <div className="w-2 h-2 rounded-full bg-[#0EA5E9]" data-id="element-10361" data-mp="1ck3qqpw3q7u5" />}
                      </div>
                      <span className="text-sm font-medium text-[#0F172A]" data-id="element-10362" data-mp="1he7xb81ggdup">
                        {option.title}
                      </span>
                    </label>
                    <p className="text-xs text-[#64748B] ml-7" data-id="element-10363" data-mp="1qbi7sr6g7yyk">{option.desc}</p>
                    {option.id === 'custom' && qualificationWindow === 'custom' && <div className="ml-7 mt-2" data-id="element-10364" data-mp="1va3cb2sqh4st">
                          <label className="block text-xs font-medium text-[#475569] mb-1.5" htmlFor="custom-start-month" data-id="element-10365" data-mp="209o9b7ocf32w">
                            Window starts
                          </label>
                          <div className="relative w-[200px]" data-id="element-10366" data-mp="5xrd71eyh91i">
                            <select id="custom-start-month" value={customStartMonth} onChange={e => setCustomStartMonth(e.target.value)} className="w-full h-9 pl-3 pr-8 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none appearance-none bg-white" data-id="element-10367" data-mp="3uil30g2p6tgf">
                              {MONTHS.map(month => <option key={month} value={month} data-id="element-10368" data-mp="3fvi17x7cdyna">
                                  {month}
                                </option>)}
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8] pointer-events-none" data-id="element-10369" data-mp="cejeffcinw54" />
                          </div>
                        </div>}
                  </div>)}
              </div>
            </div>}

          {/* Downgrade Policy — needs somewhere to move up or down to */}
          {!isSingleTier && <div className="mb-6" data-id="element-10370" data-mp="2n59km9sgfmat">
            <span className="text-sm font-medium text-[#0F172A] block mb-2" data-id="element-10371" data-mp="2n486txk20lpe">
              Downgrade Policy
            </span>
            <p className="text-xs text-[#64748B] mb-3" data-id="element-10372" data-mp="1f6c91wpx0gro">
              Members move up as soon as they qualify.
            </p>
            <div className="flex items-center gap-6" data-id="element-10373" data-mp="1j535e1mk3udq">
              <label className="flex items-center gap-2 cursor-pointer" data-id="element-10374" data-mp="156v118n6xqsj">
                <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${downgradePolicy === 'permanent' ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} onClick={() => setDowngradePolicy('permanent')} data-id="element-10375" data-mp="1a67x8jzyca7s">
                  {downgradePolicy === 'permanent' && <div className="w-2 h-2 rounded-full bg-[#0EA5E9]" data-id="element-10376" data-mp="10ao6ec08w1pw" />}
                </div>
                <span className="text-sm text-[#0F172A]" data-id="element-10377" data-mp="mf28tgfy49lv">
                  Once earned, permanent
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer" data-id="element-10378" data-mp="cih4kxbr32j3">
                <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${downgradePolicy === 'periodic' ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} onClick={() => setDowngradePolicy('periodic')} data-id="element-10379" data-mp="hi2eg2sgkval">
                  {downgradePolicy === 'periodic' && <div className="w-2 h-2 rounded-full bg-[#0EA5E9]" data-id="element-10380" data-mp="3q1e3s2otzw88" />}
                </div>
                <span className="text-sm text-[#0F172A]" data-id="element-10381" data-mp="7v99l9l0kx73">
                  Re-evaluate periodically
                </span>
              </label>
            </div>

            {downgradePolicy === 'periodic' && <div className="mt-3 ml-6" data-id="element-10382" data-mp="gttmfu3w8cne">
                {isFixedWindow ? <p className="text-xs text-[#64748B]" data-id="element-10383" data-mp="ctswbc7fmd0i">
                    Tier is reviewed for downgrade at period close.
                  </p> : <>
                    <span className="text-xs font-medium text-[#475569] block mb-2" data-id="element-10384" data-mp="qkfy5d8hoj0t">
                      How often tiers are reviewed for downgrade
                    </span>
                    <div className="flex items-center gap-6" data-id="element-10385" data-mp="10k6px75o0jx7">
                      {[{
                  id: 'daily',
                  label: 'Daily'
                }, {
                  id: 'monthly',
                  label: 'Monthly'
                }].map(option => <label key={option.id} className="flex items-center gap-2 cursor-pointer" data-id="element-10386" data-mp="2p0x1jj60gajp">
                          <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${reviewFrequency === option.id ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} onClick={() => setReviewFrequency(option.id)} data-id="element-10387" data-mp="2tzhx35abi61t">
                            {reviewFrequency === option.id && <div className="w-2 h-2 rounded-full bg-[#0EA5E9]" data-id="element-10388" data-mp="265gfsrtbm6yb" />}
                          </div>
                          <span className="text-sm text-[#0F172A]" data-id="element-10389" data-mp="2b38j0hzf1fht">
                            {option.label}
                          </span>
                        </label>)}
                    </div>
                  </>}
              </div>}
          </div>}

          {/* Tier Rows */}
          <div className="space-y-0" data-id="element-10390" data-mp="3rgqbe065f4hu">
            {tiers.map(tier => {
            const ev = editValues[tier.id];
            return <div key={tier.id} ref={el => {
              tierRowRefs.current[tier.id] = el;
            }} className={`rounded-md transition-all duration-300 ${highlightedTierId === tier.id ? 'ring-2 ring-[#0EA5E9] bg-[#F0F9FF]' : ''}`} data-id="element-10391" data-mp="d1m6fv2b5ej">
                  <div className="flex items-center gap-4 py-3 border-b border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors group relative" data-id="element-10392" data-mp="1b2mzbt3s09sa">
                    <GripVertical className="w-4 h-4 text-[#94A3B8] cursor-grab" data-id="element-10393" data-mp="163tj3sx3u4of" />
                    <div className="w-3 h-3 rounded-full" style={{
                  backgroundColor: tier.color
                }} data-id="element-10394" data-mp="115s2den5ihg4" />
                    <div className="flex-1 min-w-0" data-id="element-10395" data-mp="x5rmdpkely6y">
                      <span className="block text-sm font-medium text-[#0F172A]" data-id="element-10396" data-mp="s5df49qmms9z">
                        {tier.name || <span className="text-[#94A3B8] italic" data-id="element-10397" data-mp="n6937bcbigfy">New Tier</span>}
                      </span>
                      {(() => {
                    const summary = summariseTierBenefits(tier.discount, tier.freeShipping);
                    return summary ? <span className="block text-xs text-[#64748B] mt-0.5 truncate" title={summary} data-id="element-10398" data-mp="2hhfie6wohmno">
                            {summary}
                          </span> : null;
                  })()}
                    </div>
                    <span className="text-sm text-[#475569] w-24 text-right" data-id="element-10399" data-mp="5wewdxzvjj9k">
                      {isPointsMethod ? `${tier.threshold.toLocaleString()} pts` : <>${tier.threshold.toLocaleString()}</>}
                    </span>
                    <span className="text-sm text-[#475569] w-16 text-right" data-id="element-10400" data-mp="ylvmuq3xoq35">
                      {tier.multiplier}x
                    </span>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-10401" data-mp="omekoinw4ubx">
                      <button onClick={() => {
                    if (expandedTierId === tier.id) {
                      finishEditing(tier.id);
                    } else {
                      startEditing(tier);
                    }
                  }} className="p-1.5 rounded hover:bg-[#E0F2FE] text-[#94A3B8] hover:text-[#0EA5E9] transition-colors" data-id="element-10402" data-mp="tky70k6ec84t">
                        <Pencil className="w-4 h-4" data-id="element-10403" data-mp="1wg6edxpzxppm" />
                      </button>
                      <button onClick={() => setDeletingTierId(deletingTierId === tier.id ? null : tier.id)} className="p-1.5 rounded hover:bg-[#FEE2E2] text-[#94A3B8] hover:text-[#EF4444] transition-colors" data-id="element-10404" data-mp="1reh3qj6ore22">
                        <Trash2 className="w-4 h-4" data-id="element-10405" data-mp="21dy7gv2dnhve" />
                      </button>
                    </div>

                    {/* Delete Confirmation Popover */}
                    {deletingTierId === tier.id && <div className="absolute right-0 top-full mt-1 w-72 bg-white border border-[#E2E8F0] rounded-lg shadow-lg p-4 z-20" data-id="element-10406" data-mp="18qc15f1rlz4u">
                        <p className="text-sm text-[#0F172A] mb-1 font-medium" data-id="element-10407" data-mp="1hpfj2fp748hr">
                          Delete this tier?
                        </p>
                        <p className="text-xs text-[#64748B] mb-3" data-id="element-10408" data-mp="txs2aqlnedt7">
                          Members will be moved to the tier below.
                        </p>
                        <div className="flex justify-end gap-2" data-id="element-10409" data-mp="1etk9uzul161w">
                          <Button variant="ghost" size="sm" onClick={() => setDeletingTierId(null)} data-id="element-10410" data-mp="1jrvjb9gytejl">
                            Cancel
                          </Button>
                          <Button variant="danger" size="sm" onClick={() => deleteTier(tier.id)} data-id="element-10411" data-mp="1ns5owl0ui2xx">
                            Delete
                          </Button>
                        </div>
                      </div>}
                  </div>

                  {/* Expanded Edit State */}
                  {expandedTierId === tier.id && ev && <div key={`${tier.id}-${editSession}`} className="ml-8 mt-3 mb-3 p-4 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]" data-id="element-10412" data-mp="27hg1hq5ao7rt">
                      <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-10413" data-mp="2bhzzsx1lthfj">
                        <div data-id="element-10414" data-mp="2gie9skmrn1g7">
                          <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-10415" data-mp="2gj7ab3v4supw">
                            Name <span className="text-[#EF4444]" data-id="element-10416" data-mp="2liaaa9w98ar0">*</span>
                          </label>
                          <input type="text" value={ev.name} onChange={e => updateEditValue(tier.id, 'name', e.target.value)} placeholder="Tier name" className="w-full h-9 px-3 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-10417" data-mp="2vlywhxzp6476" />
                        </div>
                        <div data-id="element-10418" data-mp="2hnr4m4hipt2v">
                          <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-10419" data-mp="3ec6xa9fxcik7">
                            Threshold
                          </label>
                          <div className="relative" data-id="element-10420" data-mp="39ewxbzuwsoi4">
                            {!isPointsMethod && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#94A3B8]" data-id="element-10421" data-mp="34k13qva0xt2w">
                                $
                              </span>}
                            {isPointsMethod && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#94A3B8]" data-id="element-10422" data-mp="y2318s6gcfn">
                                pts
                              </span>}
                            <input type="number" value={ev.threshold} onChange={e => updateEditValue(tier.id, 'threshold', e.target.value)} className={`w-full h-9 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none ${isPointsMethod ? 'pl-3 pr-10' : 'pl-7 pr-3'}`} data-id="element-10423" data-mp="1rjk9ttwovmmz" />
                          </div>
                        </div>
                        <div data-id="element-10424" data-mp="18v5ggzywfwj0">
                          <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-10425" data-mp="13uhitvg60hft">
                            Multiplier
                          </label>
                          <div className="relative" data-id="element-10426" data-mp="yvwq0h38t01e">
                            <input type="number" value={ev.multiplier} onChange={e => updateEditValue(tier.id, 'multiplier', e.target.value)} step="0.1" className="w-full h-9 pl-3 pr-7 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-10427" data-mp="uub7af411rja" />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#94A3B8]" data-id="element-10428" data-mp="az09uuo8xz0d">
                              x
                            </span>
                          </div>
                        </div>
                        <div data-id="element-10429" data-mp="krz68ypzituj">
                          <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-10430" data-mp="or7yfz5dmjnz">
                            Colour
                          </label>
                          <div className="flex items-center gap-2" data-id="element-10431" data-mp="3o8o5s0r5h3ap">
                            {COLOR_OPTIONS.map(c => <button key={c.value} onClick={() => updateEditValue(tier.id, 'color', c.value)} className={`w-7 h-7 rounded-full border-2 transition-all ${ev.color === c.value ? 'border-[#0F172A] scale-110' : 'border-transparent hover:scale-105'}`} style={{
                        backgroundColor: c.value
                      }} title={c.name} data-id="element-10432" data-mp="3t5y280cyif81" />)}
                          </div>
                        </div>
                      </div>

                      <TierBenefitsEditor key={`${tier.id}-${editSession}`} discount={ev.discount} freeShipping={ev.freeShipping} onChange={patch => updateEditPerks(tier.id, patch)} data-id="element-10433" data-mp="3diicy870j110" />

                      {/* Perks */}
                      <div className="mb-4" data-id="element-10434" data-mp="3ng4vd4nitapq">
                        <label className="block text-xs font-medium text-[#475569] mb-1" data-id="element-10435" data-mp="3nf3g0nzh2227">
                          Perks
                        </label>
                        <p className="text-xs text-[#64748B] mb-2" data-id="element-10436" data-mp="2utk5k8mp16oa">
                          Shown to members as part of the tier. Not applied
                          automatically.
                        </p>
                        {ev.benefits.length > 0 && <ul className="mb-2" data-id="element-10437" data-mp="34keszgboecbb">
                            {ev.benefits.map(benefit => <li key={benefit} className="flex items-center justify-between gap-3 py-1.5 border-b border-[#E2E8F0] last:border-b-0" data-id="element-10438" data-mp="2zqk9acw2lxc8">
                                <span className="text-sm text-[#0F172A]" data-id="element-10439" data-mp="273pu9vi0vq65">
                                  {benefit}
                                </span>
                                <button type="button" onClick={() => removeBenefit(tier.id, benefit)} aria-label={`Remove ${benefit}`} className="p-1 rounded hover:bg-[#FEE2E2] text-[#94A3B8] hover:text-[#EF4444] transition-colors flex-shrink-0" data-id="element-10440" data-mp="mj2hutlbk3c3">
                                  <X className="w-3.5 h-3.5" data-id="element-10441" data-mp="hlasa685ctb2" />
                                </button>
                              </li>)}
                          </ul>}
                        {addingBenefitForTier === tier.id ? <div className="flex items-center gap-1" data-id="element-10442" data-mp="1aazw0rc8r7gd">
                            <input type="text" value={newBenefitText} onChange={e => setNewBenefitText(e.target.value)} onKeyDown={e => {
                      if (e.key === 'Enter') addBenefit(tier.id);
                    }} placeholder="Perk" aria-label="New perk" autoFocus className="h-9 flex-1 px-3 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-10443" data-mp="waysf44ldlx7" />
                            <button type="button" onClick={() => addBenefit(tier.id)} aria-label="Save perk" className="h-6 w-6 rounded-full bg-[#0EA5E9] text-white flex items-center justify-center hover:bg-[#0284C7] transition-colors" data-id="element-10444" data-mp="55l0x8fr0snp">
                              <Check className="w-3 h-3" data-id="element-10445" data-mp="3ri1ju5ksxmi2" />
                            </button>
                            <button type="button" onClick={() => {
                      setAddingBenefitForTier(null);
                      setNewBenefitText('');
                    }} aria-label="Cancel" className="h-6 w-6 rounded-full text-[#94A3B8] flex items-center justify-center hover:text-[#EF4444] transition-colors" data-id="element-10446" data-mp="3ngfydvu7bzw9">
                              <X className="w-3 h-3" data-id="element-10447" data-mp="a3wcbnlozb81" />
                            </button>
                          </div> : <Button variant="ghost" size="sm" iconLeft={<Plus className="w-4 h-4" data-id="element-10449" data-mp="2ztpx4wpog9cp" />} onClick={() => setAddingBenefitForTier(tier.id)} data-id="element-10448" data-mp="2zsomuughjqw1">
                            Add perk
                          </Button>}
                      </div>

                      <div className="flex items-center justify-between" data-id="element-10450" data-mp="1addwgnmems1n">
                        <span className="text-xs text-[#EF4444]" data-id="element-10451" data-mp="1fa4910z035ib">
                          {!ev.name.trim() ? 'Tier name is required' : ''}
                        </span>
                        <div className="flex items-center gap-2" data-id="element-10452" data-mp="1o2z8fe1se706">
                          <Button variant="ghost" size="sm" onClick={() => cancelEditing(tier.id)} data-id="element-10453" data-mp="1o5bzcgpivrpd">
                            Cancel
                          </Button>
                          <Button variant="primary" size="sm" disabled={!ev.name.trim()} onClick={() => finishEditing(tier.id)} data-id="element-10454" data-mp="1y03wxkj3uek5">
                            Done
                          </Button>
                        </div>
                      </div>
                    </div>}
                </div>;
          })}
          </div>

          <div className="mt-4 flex items-center gap-2" data-id="element-10455" data-mp="8oa3t7hskbdy">
            <Button variant="ghost" size="sm" onClick={addNewTier} disabled={tiers.length >= 5} iconLeft={<Plus className="w-4 h-4" data-id="element-10457" data-mp="1fy6iunxlo3zk" />} data-id="element-10456" data-mp="cnammq09re0y">
              Add Tier
            </Button>
            <span className="text-xs text-[#94A3B8]" data-id="element-10458" data-mp="1b053oe3aootd">(max 5)</span>
          </div>
        </div>

        {/* SECTION 2 — BASE EARNING RATE */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 shadow-sm" data-id="element-10459" data-mp="1p17oyomgtv38">
          <h2 className="text-base font-semibold text-[#0F172A] mb-4" data-id="element-10460" data-mp="1kwzvyf8z5sz7">
            Base Earning Rate
          </h2>
          <div className="flex items-center gap-4" data-id="element-10461" data-mp="1yw9fnowe357x">
            <span className="text-sm font-medium text-[#0F172A]" data-id="element-10462" data-mp="1tz0x3n7bwz65">
              Points per dollar spent
            </span>
            <input type="number" min="0" value={baseEarningRate} onChange={e => setBaseEarningRate(e.target.value)} className="w-20 h-10 text-center border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-10463" data-mp="28mlv52pck877" />
          </div>
          {(() => {
          const rate = parseFloat(baseEarningRate);
          if (baseEarningRate.trim() === '' || isNaN(rate)) {
            return <p className="text-xs text-[#EF4444] mt-2" data-id="element-10464" data-mp="2w8xuhlwojj4j">
                  Enter a points-per-dollar rate
                </p>;
          }
          if (rate < 0) {
            return <p className="text-xs text-[#EF4444] mt-2" data-id="element-10465" data-mp="2n9knr0zxx8ni">
                  Rate can't be negative
                </p>;
          }
          // Tier multipliers compound the base rate, so the true give-back is the
          // base rebate times the highest multiplier in play.
          const topMultiplier = tiers.reduce((max, tier) => Math.max(max, tier.multiplier || 1), 1);
          const baseRebate = rate * REDEMPTION_VALUE_PER_POINT * 100;
          const topTierRebate = baseRebate * topMultiplier;
          return <>
                <p className="text-xs text-[#94A3B8] mt-2" data-id="element-10466" data-mp="28j3l5ebkkcp1">
                  A $50 order earns {(50 * rate).toLocaleString()} points
                  (before tier multipliers)
                </p>
                <div className="mt-4 bg-[#F0F9FF] border border-[#BAE6FD] rounded-md px-4 py-3" data-id="element-10467" data-mp="1zmkts6izkwuk">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-10468" data-mp="2f0os9ywzb1g2">
                    <div className="bg-white border border-[#BAE6FD] rounded-md px-3 py-2" data-id="element-10469" data-mp="2a47use3w3aqb">
                      <div className="text-xs font-medium text-[#0369A1] uppercase tracking-wider" data-id="element-10470" data-mp="253aiuj9h3cx9">
                        Base rebate
                      </div>
                      <div className="text-lg font-semibold text-[#0F172A] mt-0.5" data-id="element-10471" data-mp="1w4fap1mvbsrb">
                        {baseRebate.toFixed(1)}%
                      </div>
                      <div className="text-xs text-[#64748B]" data-id="element-10472" data-mp="1rb3zypexqru4">
                        Every member at 1× points
                      </div>
                    </div>
                    <div className="bg-white border border-[#BAE6FD] rounded-md px-3 py-2" data-id="element-10473" data-mp="1hgc0th5pfwx8">
                      <div className="text-xs font-medium text-[#0369A1] uppercase tracking-wider" data-id="element-10474" data-mp="1dce2h8o7eq58">
                        Top tier rebate
                      </div>
                      <div className="text-lg font-semibold text-[#0F172A] mt-0.5" data-id="element-10475" data-mp="3k1y5bg4bl02z">
                        {topTierRebate.toFixed(1)}%
                      </div>
                      <div className="text-xs text-[#64748B]" data-id="element-10476" data-mp="3p2vnvtt8rw50">
                        Assumes {topMultiplier}× top tier multiplier
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-[#64748B] mt-3 mb-0" data-id="element-10477" data-mp="1tp8s9i95a3o">
                    Calculated at ${REDEMPTION_VALUE_PER_POINT.toFixed(2)} per
                    point. Redemption value is set in Program Settings.
                  </p>
                </div>
                {topTierRebate > 12 && <div className="mt-3 bg-[#FEF3C7] border border-[#FDE68A] rounded-md px-4 py-3 flex items-start gap-3" data-id="element-10478" data-mp="3aak2ozh2nk5m">
                    <AlertTriangle className="w-4 h-4 text-[#D97706] flex-shrink-0 mt-0.5" data-id="element-10479" data-mp="3f4ft9gv0a4qr" />
                    <p className="text-xs text-[#92400E] m-0" data-id="element-10480" data-mp="2hld2zx5vsois">
                      At {rate} points per $1 and a {topMultiplier}× top tier
                      multiplier, you're giving back{' '}
                      {topTierRebate.toFixed(1)}% of order value to your best
                      customers. Worth checking that against your wine margins.
                    </p>
                  </div>}
              </>;
        })()}
        </div>

        {/* SECTION 3 — PRODUCT EARNING MODEL */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 shadow-sm" data-id="element-10481" data-mp="1scw3kcwj4tsf">
          <h2 className="text-base font-semibold text-[#0F172A] mb-4" data-id="element-10482" data-mp="1dkklhrfnoxx9">
            Product Earning Model
          </h2>
          <div className="space-y-4" data-id="element-10483" data-mp="1ieggcl0mi4o7">
            {[{
            id: 'everything',
            title: 'Earn on Everything',
            desc: 'All products earn at the base rate'
          }, {
            id: 'selected',
            title: 'Earn on Selected Only',
            desc: 'Only whitelisted collections/products earn'
          }, {
            id: 'exclude',
            title: 'Exclude Selected',
            desc: 'Everything earns except blacklisted items'
          }, {
            id: 'tiered',
            title: 'Tiered by Collection',
            desc: 'Different rates per collection'
          }].map(option => <div key={option.id} data-id="element-10484" data-mp="pz1hld1u0q2r">
                <label className="flex items-center gap-3 cursor-pointer mb-1" data-id="element-10485" data-mp="pz9ydg12sfj8">
                  <div className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${earningModel === option.id ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} onClick={() => setEarningModel(option.id)} data-id="element-10486" data-mp="kydsiwx5yjb6">
                    {earningModel === option.id && <div className="w-2 h-2 rounded-full bg-[#0EA5E9]" data-id="element-10487" data-mp="18p00ko7643l6" />}
                  </div>
                  <span className="text-sm font-medium text-[#0F172A]" data-id="element-10488" data-mp="18quehik0uvfr">
                    {option.title}
                  </span>
                </label>
                <p className="text-xs text-[#64748B] ml-7" data-id="element-10489" data-mp="1hqhfw4cr418t">{option.desc}</p>
              </div>)}
          </div>

          {/* Conditional config for the chosen earning model */}
          {(earningModel === 'selected' || earningModel === 'exclude') && <div className="mt-5 pt-5 border-t border-[#E2E8F0]" data-id="element-10490" data-mp="1kpg5okuc0hdo">
              <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-10491" data-mp="1pqvp68gv2fty">
                {earningModel === 'selected' ? 'Products that earn points' : "Products that don't earn points"}
              </label>
              <p className="text-xs text-[#94A3B8] mb-3" data-id="element-10492" data-mp="1zi9wh2f20jxn">
                Choose by product tag — any product carrying one of these tags{' '}
                {earningModel === 'selected' ? 'will earn points.' : 'will be excluded from earning.'}{' '}
                Tag products from the Products page.
              </p>
              <div className="flex flex-wrap items-center gap-2" data-id="element-10493" data-mp="2if1b2yjniavd">
                {earningTags.map(tag => <div key={tag} className="flex items-center gap-1 bg-[#E0F2FE] text-[#0EA5E9] px-2.5 py-1 rounded-full text-xs font-medium" data-id="element-10494" data-mp="2icowmvd3qin2">
                    {tag}
                    <button onClick={() => removeEarningTag(tag)} className="hover:text-[#0369A1]" data-id="element-10495" data-mp="o1jvbxphmiaw">
                      <X className="w-3 h-3" data-id="element-10496" data-mp="31cuv5q0nag0k" />
                    </button>
                  </div>)}
                <input type="text" value={newEarningTag} onChange={e => setNewEarningTag(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addEarningTag();
              }
            }} placeholder="Add a tag" className="h-7 w-32 px-2 border border-[#CBD5E1] rounded-md text-xs text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-10497" data-mp="2wj08qetnnmxs" />
              </div>
              {SUGGESTED_PRODUCT_TAGS.filter(t => !earningTags.includes(t)).length > 0 && <div className="flex flex-wrap items-center gap-2 mt-3" data-id="element-10498" data-mp="36amuh53md4es">
                  <span className="text-xs text-[#94A3B8]" data-id="element-10499" data-mp="36ch8s0kdxy9w">Suggested:</span>
                  {SUGGESTED_PRODUCT_TAGS.filter(t => !earningTags.includes(t)).map(tag => <button key={tag} onClick={() => addEarningTag(tag)} className="flex items-center gap-1 border border-dashed border-[#CBD5E1] text-[#64748B] px-2.5 py-1 rounded-full text-xs hover:border-[#0EA5E9] hover:text-[#0EA5E9] transition-colors" data-id="element-10500" data-mp="1uuy5672w7tat">
                      <Plus className="w-3 h-3" data-id="element-10501" data-mp="3nkgo48uc8oww" />
                      {tag}
                    </button>)}
                </div>}
              {earningTags.length === 0 && <p className="text-xs text-[#EF4444] mt-3" data-id="element-10502" data-mp="3er3rowx2f562">
                  Add at least one tag, or no products will{' '}
                  {earningModel === 'selected' ? 'earn' : 'be excluded'}.
                </p>}
            </div>}

          {earningModel === 'tiered' && <div className="mt-5 pt-5 border-t border-[#E2E8F0]" data-id="element-10503" data-mp="3si8hz8ejml3q">
              <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-10504" data-mp="3sjtg1802gm6c">
                Collection rates
              </label>
              <p className="text-xs text-[#94A3B8] mb-3" data-id="element-10505" data-mp="2h20n7t5cse2m">
                Set a points-per-dollar rate for specific collections. Anything
                not listed earns at the base rate.
              </p>
              {tieredCollections.length > 0 && <div className="space-y-2 mb-3" data-id="element-10506" data-mp="2egkj8qqbt5cs">
                  {tieredCollections.map(row => {
              const used = tieredCollections.filter(r => r.id !== row.id).map(r => r.collection);
              return <div key={row.id} className="flex items-center gap-2" data-id="element-10507" data-mp="2niac80zuwcco">
                        <div className="relative flex-1" data-id="element-10508" data-mp="2iks76loymh9y">
                          <select value={row.collection} onChange={e => updateTieredCollection(row.id, 'collection', e.target.value)} className="w-full h-9 pl-3 pr-8 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none appearance-none bg-white" data-id="element-10509" data-mp="1uu4r3d4w4lcb">
                            <option value="" data-id="element-10510" data-mp="1zsze6r9w4w28">Select a collection…</option>
                            {SHOPIFY_COLLECTIONS.filter(c => c === row.collection || !used.includes(c)).map(c => <option key={c} value={c} data-id="element-10511" data-mp="1c1ajx5didxin">
                                {c}
                              </option>)}
                          </select>
                          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8] pointer-events-none" data-id="element-10512" data-mp="376a6fiwm7yge" />
                        </div>
                        <div className="relative w-32" data-id="element-10513" data-mp="3gwcxhdk6f2c3">
                          <input type="number" min="0" value={row.rate} onChange={e => updateTieredCollection(row.id, 'rate', e.target.value)} placeholder="Rate" className="w-full h-9 pl-3 pr-12 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-10514" data-mp="3gyz51d72hhub" />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#94A3B8]" data-id="element-10515" data-mp="2y5luctqs8865">
                            pts/$
                          </span>
                        </div>
                        <button onClick={() => removeTieredCollection(row.id)} aria-label="Remove collection" className="w-7 h-7 flex items-center justify-center rounded-md text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#FEE2E2] transition-colors flex-shrink-0" data-id="element-10516" data-mp="dgnzsve86xof">
                          <X className="w-4 h-4" data-id="element-10517" data-mp="221czh1fx3be8" />
                        </button>
                      </div>;
            })}
                </div>}
              <Button variant="ghost" size="sm" onClick={addTieredCollection} disabled={tieredCollections.length >= SHOPIFY_COLLECTIONS.length} iconLeft={<Plus className="w-4 h-4" data-id="element-10519" data-mp="1o7vklty6emuj" />} data-id="element-10518" data-mp="1t6f83ysf46p0">
                Add Collection
              </Button>
              <p className="text-xs text-[#94A3B8] mt-3" data-id="element-10520" data-mp="1jac1afmmfjvq">
                If a product sits in two listed collections, the higher rate
                applies.
              </p>
            </div>}
        </div>

        {/* SECTION 4 — BONUS EVENTS */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 shadow-sm" data-id="element-10521" data-mp="159t1tww81m56">
          <h2 className="text-base font-semibold text-[#0F172A] mb-4" data-id="element-10522" data-mp="34m3qsdyn2jk0">
            Bonus Events
          </h2>
          <div className="space-y-0" data-id="element-10523" data-mp="2zmr907mmk2oa">
            {bonusEvents.map(event => <div key={event.id} className="flex items-center justify-between py-3 border-b border-[#E2E8F0]" data-id="element-10524" data-mp="2zlg424hg0ovl">
                <div className="flex-1" data-id="element-10525" data-mp="2dh1fkb5hmbyz">
                  {event.custom ? <input type="text" value={event.name} onChange={e => updateMilestoneName(event.id, e.target.value)} placeholder="Milestone name" className="w-full max-w-xs h-8 px-2 border border-[#CBD5E1] rounded-md text-sm font-medium text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-10526" data-mp="2dghzz13b7zo4" /> : <>
                      <span className="text-sm font-medium text-[#0F172A]" data-id="element-10527" data-mp="1tt26d4qkncvo">
                        {event.name}
                      </span>
                      {event.note && <span className="text-xs text-[#94A3B8] ml-2" data-id="element-10528" data-mp="1fuu3kg6uthcl">
                          ({event.note})
                        </span>}
                    </>}
                </div>
                <div className="flex items-center gap-4" data-id="element-10529" data-mp="3a8lk7znlp1hj">
                  <button onClick={() => toggleBonus(event.id)} className={`w-11 h-6 rounded-full transition-colors duration-200 ease-in-out relative ${bonusToggles[event.id] ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-10530" data-mp="3f64xaw8kb87w">
                    <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ease-in-out ${bonusToggles[event.id] ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-10531" data-mp="ykgokdukba5d" />
                  </button>
                  <div className="flex items-center gap-2" data-id="element-10532" data-mp="18gjrhlx8nctd">
                    <input type="number" defaultValue={event.points} className="w-20 h-8 text-center border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-10533" data-mp="1m8zw2r5sfiwe" />
                    <span className="text-sm text-[#475569] w-6" data-id="element-10534" data-mp="1w771qdummej3">pts</span>
                  </div>
                  {event.custom && (event.confirmed ? <button onClick={() => removeMilestone(event.id)} aria-label="Remove milestone" className="w-7 h-7 flex items-center justify-center rounded-md text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#FEE2E2] transition-colors" data-id="element-10535" data-mp="1r6rl9df1sqww">
                        <X className="w-4 h-4" data-id="element-10536" data-mp="1pyv5zwacto8g" />
                      </button> : <div className="flex items-center gap-1" data-id="element-10537" data-mp="1l0tohfsktwz3">
                        <button onClick={() => confirmMilestone(event.id)} disabled={!event.name.trim()} aria-label="Add milestone" className="w-7 h-7 flex items-center justify-center rounded-md bg-[#0EA5E9] text-white hover:bg-[#0284C7] transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-10538" data-mp="1l0afiokwmix7">
                          <Check className="w-4 h-4" data-id="element-10539" data-mp="171arnj57u823" />
                        </button>
                        <button onClick={() => removeMilestone(event.id)} aria-label="Discard milestone" className="w-7 h-7 flex items-center justify-center rounded-md text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#FEE2E2] transition-colors" data-id="element-10540" data-mp="123ip7v6y33jn">
                          <X className="w-4 h-4" data-id="element-10541" data-mp="2sedn9ny5irkn" />
                        </button>
                      </div>)}
                </div>
              </div>)}
          </div>
          <div className="mt-4" data-id="element-10542" data-mp="2nf0z8zvjnegh">
            <Button variant="ghost" size="sm" onClick={addCustomMilestone} iconLeft={<Plus className="w-4 h-4" data-id="element-10544" data-mp="2v0wf0pf7fmm6" />} data-id="element-10543" data-mp="2uytg1fw2qj5b">
              Add Custom Milestone
            </Button>
          </div>
        </div>

        {/* SECTION 5 — EXPERIENCE-BASED EARNING */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 shadow-sm" data-id="element-10545" data-mp="2lzpu845ivpn4">
          <div className="flex items-center justify-between mb-4" data-id="element-10546" data-mp="2q0rzp2q18xxj">
            <h2 className="text-base font-semibold text-[#0F172A]" data-id="element-10547" data-mp="3dpkyd6y1hjvu">
              Experience-Based Earning
            </h2>
            <div className="flex items-center gap-3" data-id="element-10548" data-mp="3incmdpvkclur">
              <span className="text-sm font-medium text-[#0F172A]" data-id="element-10549" data-mp="3iq8olqeg1wp0">
                Enabled
              </span>
              <button onClick={() => setExperienceEnabled(!experienceEnabled)} className={`w-11 h-6 rounded-full transition-colors duration-200 ease-in-out relative ${experienceEnabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-10550" data-mp="38yclu21exieg">
                <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ease-in-out ${experienceEnabled ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-10551" data-mp="59rdkfwodm8z" />
              </button>
            </div>
          </div>

          <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-id="element-10552" data-mp="b221ap5lv710">
            <table className="w-full" data-id="element-10553" data-mp="62yvv3lpqysi">
              <thead className="bg-[#F1F5F9]" data-id="element-10554" data-mp="15ywtcna6waw">
                <tr data-id="element-10555" data-mp="1570fzlv0ujk">
                  <th className="py-2 px-3 text-left text-xs uppercase tracking-wider font-semibold text-[#94A3B8]" data-id="element-10556" data-mp="17td2dnly08n">
                    Experience Type
                  </th>
                  <th className="py-2 px-3 text-right text-xs uppercase tracking-wider font-semibold text-[#94A3B8] w-24" data-id="element-10557" data-mp="3njqj48hvavvu">
                    Base Points
                  </th>
                  <th className="py-2 px-3 text-center text-xs uppercase tracking-wider font-semibold text-[#94A3B8] w-32" data-id="element-10558" data-mp="3elo7kz0ickub">
                    Tier Multiplier Applied
                  </th>
                  <th className="py-2 px-3 w-16" aria-hidden="true" data-id="element-10559" data-mp="34m5r4sjt3h70"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]" data-id="element-10560" data-mp="2uhpb9z6bu037">
                {experienceTypes.map((exp, index) => <tr key={index} data-id="element-10561" data-mp="2zgadu591wvut" data-mpi={index}>
                    <td className="py-3 px-3 text-sm text-[#0F172A]" data-id="element-10562" data-mp="2zjodv6u8ssz3">
                      {exp.custom ? <input type="text" value={exp.name} onChange={e => updateExperienceName(index, e.target.value)} placeholder="Experience name" className="w-full max-w-xs h-8 px-2 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-10563" data-mp="34hg661ysuh3t" /> : exp.name}
                    </td>
                    <td className="py-3 px-3" data-id="element-10564" data-mp="2lpx540oc5d4f">
                      <input type="number" defaultValue={exp.points} className="w-20 h-8 text-center border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none float-right" data-id="element-10565" data-mp="2lo3xoj5weovd" />
                    </td>
                    <td className="py-3 px-3 text-center" data-id="element-10566" data-mp="1xzb7l3byjb8o">
                      <span className="text-sm font-medium text-[#10B981]" data-id="element-10567" data-mp="3k4ed0kx0r0b">
                        Yes
                      </span>
                    </td>
                    <td className="py-3 px-3 text-right" data-id="element-10568" data-mp="3usi8xqqae314">
                      {exp.custom && (exp.confirmed ? <button onClick={() => removeExperienceType(index)} aria-label="Remove experience type" className="w-7 h-7 inline-flex items-center justify-center rounded-md text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#FEE2E2] transition-colors" data-id="element-10569" data-mp="dbs0clypdfue">
                            <X className="w-4 h-4" data-id="element-10570" data-mp="i9tmxgvc7xam" />
                          </button> : <div className="flex items-center justify-end gap-1" data-id="element-10571" data-mp="ibmrmqme47fz">
                            <button onClick={() => confirmExperience(index)} disabled={!exp.name.trim()} aria-label="Add experience type" className="w-7 h-7 flex items-center justify-center rounded-md bg-[#0EA5E9] text-white hover:bg-[#0284C7] transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-10572" data-mp="w8a09qnpgm1q">
                              <Check className="w-4 h-4" data-id="element-10573" data-mp="2t3fts7eicq8g" />
                            </button>
                            <button onClick={() => removeExperienceType(index)} aria-label="Discard experience type" className="w-7 h-7 flex items-center justify-center rounded-md text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#FEE2E2] transition-colors" data-id="element-10574" data-mp="18ldqmfkfmlm6">
                              <X className="w-4 h-4" data-id="element-10575" data-mp="13riyqouob0c0" />
                            </button>
                          </div>)}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="mt-4" data-id="element-10576" data-mp="uth3yr1j93x6">
            <Button variant="ghost" size="sm" onClick={addExperienceType} iconLeft={<Plus className="w-4 h-4" data-id="element-10578" data-mp="fwg9wgvvcwvj" />} data-id="element-10577" data-mp="upjk2374s8c8">
              Add Experience Type
            </Button>
          </div>

          {/* Booking platform integration — automatic experience earning */}
          <div className="mt-6 pt-6 border-t border-[#E2E8F0]" data-id="element-10579" data-mp="2a965d31vye51">
            <div className="flex items-start justify-between" data-id="element-10580" data-mp="2f897aemkk48s">
              <div className="pr-4" data-id="element-10581" data-mp="2f5dawuqddnmg">
                <h3 className="text-sm font-semibold text-[#0F172A]" data-id="element-10582" data-mp="2dqu5dkx2ggon">
                  Earn from bookings automatically
                </h3>
                <p className="text-xs text-[#94A3B8] mt-0.5" data-id="element-10583" data-mp="28q6508my6nhr">
                  Connect a booking platform so members earn experience points
                  when their booking is attended — no manual entry.
                </p>
              </div>
              {bookingConnected && <span className="flex items-center gap-1.5 text-xs font-medium text-[#10B981] flex-shrink-0 mt-0.5" data-id="element-10584" data-mp="2wg0gomvj660f">
                  <span className="w-2 h-2 rounded-full bg-[#10B981]" data-id="element-10585" data-mp="2riifjinr8v2z" />
                  Connected
                </span>}
            </div>

            {!bookingConnected ? <div className="mt-3 flex items-center justify-between bg-[#F8FAFC] border border-[#E2E8F0] rounded-md px-4 py-3" data-id="element-10586" data-mp="2iq6s85nxtnr8">
                <div className="flex items-center gap-3" data-id="element-10587" data-mp="3f8crzz45whmp">
                  <div className="w-8 h-8 rounded-md bg-white border border-[#E2E8F0] flex items-center justify-center text-xs font-bold text-[#0F172A]" data-id="element-10588" data-mp="3ah33j1ucnkce">
                    NB
                  </div>
                  <div data-id="element-10589" data-mp="11x0b600yoxx9">
                    <p className="text-sm font-medium text-[#0F172A]" data-id="element-10590" data-mp="11yb93j7q7bgg">
                      Now Book It
                    </p>
                    <p className="text-xs text-[#94A3B8]" data-id="element-10591" data-mp="15xk56uq8nlf6">
                      Cellar door & experience bookings
                    </p>
                  </div>
                </div>
                <Button variant="secondary" size="sm" onClick={connectBooking} data-id="element-10592" data-mp="1fw9kcfqr2rrq">
                  Connect
                </Button>
              </div> : <div className="mt-3" data-id="element-10593" data-mp="1ktt41v67vwr2">
                <div className="flex items-center justify-between bg-[#F8FAFC] border border-[#E2E8F0] rounded-md px-4 py-3 mb-4" data-id="element-10594" data-mp="1pmm62baawqzg">
                  <div className="flex items-center gap-3" data-id="element-10595" data-mp="1tno78xns3d42">
                    <div className="w-8 h-8 rounded-md bg-white border border-[#E2E8F0] flex items-center justify-center text-xs font-bold text-[#0F172A]" data-id="element-10596" data-mp="1yobx3g09tnwp">
                      NB
                    </div>
                    <div data-id="element-10597" data-mp="1eif4spyxs2a3">
                      <p className="text-sm font-medium text-[#0F172A]" data-id="element-10598" data-mp="1ehx2pcpletu9">
                        Now Book It
                      </p>
                      <p className="text-xs text-[#94A3B8]" data-id="element-10599" data-mp="1nk67rn23dv09">
                        Bookings sync automatically
                      </p>
                    </div>
                  </div>
                  <button onClick={disconnectBooking} className="text-xs font-medium text-[#64748B] hover:text-[#EF4444] transition-colors" data-id="element-10600" data-mp="1jgqff2x22gtd">
                    Disconnect
                  </button>
                </div>

                <label className="block text-xs font-medium text-[#475569] mb-2" data-id="element-10601" data-mp="14l23sspeqrfg">
                  Match each booking experience to what it earns
                </label>
                <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-id="element-10602" data-mp="zshbxwq2aoek">
                  <table className="w-full" data-id="element-10603" data-mp="c36jr3ms22c1">
                    <thead className="bg-[#F1F5F9]" data-id="element-10604" data-mp="83xplxnts2fy">
                      <tr data-id="element-10605" data-mp="835u0mu8a4po">
                        <th className="py-2 px-3 text-left text-xs uppercase tracking-wider font-semibold text-[#94A3B8]" data-id="element-10606" data-mp="3u4vdie7v5g8a">
                          Now Book It experience
                        </th>
                        <th className="py-2 px-3 text-left text-xs uppercase tracking-wider font-semibold text-[#94A3B8] w-64" data-id="element-10607" data-mp="3k6pdk25mak62">
                          Earns as
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E2E8F0]" data-id="element-10608" data-mp="gra535cw0in1">
                      {NOWBOOKIT_EXPERIENCES.map(nbiExp => <tr key={nbiExp} data-id="element-10609" data-mp="lnque2vtp31p">
                          <td className="py-3 px-3 text-sm text-[#0F172A]" data-id="element-10610" data-mp="6rt0kr9dgasn">
                            {nbiExp}
                          </td>
                          <td className="py-3 px-3" data-id="element-10611" data-mp="bpuktfijag5p">
                            <div className="relative" data-id="element-10612" data-mp="2ri06znlslmmx">
                              <select value={bookingMappings[nbiExp] ?? ''} onChange={e => setBookingMapping(nbiExp, e.target.value)} className="w-full h-9 pl-3 pr-8 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none appearance-none bg-white" data-id="element-10613" data-mp="2rh8j6yyfst77">
                                <option value="" data-id="element-10614" data-mp="1y4irxfegrrb2">No points</option>
                                {experienceTypes.filter(e => e.name.trim()).map(e => <option key={e.name} value={e.name} data-id="element-10615" data-mp="280lwst0pa41p">
                                      {e.name}
                                    </option>)}
                              </select>
                              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8] pointer-events-none" data-id="element-10616" data-mp="1fbonvgidpjlm" />
                            </div>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 bg-[#F0F9FF] border border-[#BAE6FD] rounded-md px-4 py-2.5" data-id="element-10617" data-mp="rmn7n73b145s">
                  <p className="text-xs text-[#0369A1]" data-id="element-10618" data-mp="wl6uratmwlzq">
                    Points are awarded when a booking is marked attended in Now
                    Book It — not when it's booked. No-shows and cancellations
                    don't earn, and the member's tier multiplier still applies.
                  </p>
                </div>
              </div>}
          </div>
        </div>
      </div>

      {/* Toasts - replaces inline toast */}
      {toasts.map(toast => <Toast key={toast.id} {...toast} onClose={removeToast} data-id="element-10619" data-mp="a6rhp19i90ki" />)}
    </div>;
}